import type { Allegation } from "@/types"

/**
 * Shared parsing/derivation over the backend's `findings_summary_json` — the
 * JSON array built by speaking_order_check_node (backend
 * nodes/speaking_order_check/node.py::_build_findings_summary). It lands
 * MID-RUN at the speaking_order node boundary, well before the order compiles,
 * so both the live findings feed and the post-completion summary card can
 * derive from it. Moved out of OIOWorkspace so the shapes live in one place.
 */

export interface DemandComputationLite {
  cgst?: number
  sgst?: number
  igst?: number
  cess?: number
  interest?: number
  /** '' | 'not_applicable' | 'payable_not_computed' | 'computed' */
  interest_status?: string
  penalty?: number
  /** Sum of CGST+SGST+IGST+Cess — 0 for scalar-only demands (see backend). */
  total_tax?: number
  /** Total demand including tax, interest and penalty. */
  total_demand?: number
  reasoning?: string
}

export interface FindingsSummaryItem {
  allegation_id: string
  title: string
  description: string
  determination_status: string
  /** Short survival outcome, e.g. "sustained" / "dropped" (claim_survival_outcome). */
  final_conclusion?: string
  detailed_rationale: string
  taxpayer_reply_summary?: string
  cited_provisions: string[]
  demand_computation: DemandComputationLite
}

/** Safe parse of findings_summary_json → typed items (drops malformed rows). */
export function parseFindingsSummary(json: string | null | undefined): FindingsSummaryItem[] {
  if (!json) return []
  try {
    const parsed = JSON.parse(json) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (f): f is FindingsSummaryItem =>
        !!f && typeof f === "object" && typeof (f as FindingsSummaryItem).allegation_id === "string",
    )
  } catch {
    return []
  }
}

export function mapDeterminationStatus(status: string): Allegation["status"] {
  const s = (status ?? "").toLowerCase().trim()
  if (s === "confirmed") return "confirmed"
  if (s.includes("partial")) return "partial"
  if (s === "dropped" || s === "conceded" || s.includes("not_confirmed") || s.includes("not confirmed"))
    return "dropped"
  return "unknown"
}

/** Full findings → Allegation rows (with determination + demand). */
export function findingsToAllegations(findings: FindingsSummaryItem[]): Allegation[] {
  return findings.map((f) => ({
    id: f.allegation_id,
    // Prefer `description` (always the full SCN text) over `title`: older
    // drafts persisted a title hard-capped at 100 chars by the backend.
    allegation: f.description || f.title || f.allegation_id,
    status: mapDeterminationStatus(f.determination_status),
    amount: f.demand_computation?.total_demand || undefined,
    section: f.cited_provisions?.[0] || undefined,
    reasoning: f.detailed_rationale || undefined,
  }))
}

/** Verdict → design-system pill class + officer-facing label. Wash tokens:
 *  upheld (bad) / partial (warn) / favour (good) — same convention as the
 *  Analysis tab's STATUS_TO_PILL. */
export const VERDICT_PILL: Record<Allegation["status"], { cls: string; label: string }> = {
  confirmed: { cls: "upheld", label: "Confirmed" },
  partial: { cls: "partial", label: "Partly confirmed" },
  dropped: { cls: "favour", label: "Dropped" },
  unknown: { cls: "neutral", label: "Under review" },
}

/** One-line basis for a finding: the short survival outcome when present,
 *  otherwise the first sentence of the detailed rationale. */
export function findingSummaryLine(f: FindingsSummaryItem): string {
  const conclusion = (f.final_conclusion ?? "").trim()
  if (conclusion) return conclusion
  const rationale = (f.detailed_rationale ?? "").trim()
  if (!rationale) return ""
  // First sentence (lookahead, not lookbehind, for older-Safari compatibility).
  const firstSentence = rationale.match(/^.*?[.!?](?=\s|$)/)
  return firstSentence ? firstSentence[0] : rationale
}

export interface ConfirmedAggregate {
  /** Sum of total_tax over confirmed + partially-confirmed heads. */
  tax: number
  /** Sum of penalty over confirmed + partially-confirmed heads. */
  penalty: number
  /** Count of confirmed + partially-confirmed heads. */
  confirmedHeads: number
  /** True when every head with a verdict was dropped. */
  allDropped: boolean
}

/**
 * Aggregate the money that SURVIVES adjudication (confirmed + partial heads).
 * Returns null when the aggregate is not reliably derivable — a surviving head
 * with no head-wise tax split but a non-zero total_demand is a scalar-only
 * demand (tax vs interest vs penalty cannot be separated), and a wrong total
 * is worse than none. Callers must OMIT the aggregate line on null.
 */
export function aggregateConfirmedDemand(
  findings: FindingsSummaryItem[],
): ConfirmedAggregate | null {
  if (findings.length === 0) return null
  let tax = 0
  let penalty = 0
  let confirmedHeads = 0
  let decided = 0
  for (const f of findings) {
    const status = mapDeterminationStatus(f.determination_status)
    if (status === "unknown") continue
    decided += 1
    if (status === "dropped") continue
    confirmedHeads += 1
    const dc = f.demand_computation
    if (!dc || typeof dc !== "object") return null
    const totalTax = Number(dc.total_tax) || 0
    const totalDemand = Number(dc.total_demand) || 0
    if (totalTax <= 0 && totalDemand > 0) return null // scalar-only demand — no tax split
    tax += totalTax
    penalty += Number(dc.penalty) || 0
  }
  if (decided === 0) return null
  return { tax, penalty, confirmedHeads, allDropped: confirmedHeads === 0 }
}

/** Indian-style rupee formatting, matching the workspace idiom (₹ 25,66,800). */
export function formatINR(amount: number): string {
  return "₹ " + amount.toLocaleString("en-IN")
}
